import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Pencil, Trash, Plus, ArrowsClockwise, FloppyDisk, X } from "@phosphor-icons/react";
import ReferenceLibrary from "@/components/ReferenceLibrary";
import AiStatusPanel from "@/components/AiStatusPanel";
import AdvisorPlansPanel from "@/components/AdvisorPlansPanel";
import PassClaimsPanel from "@/components/PassClaimsPanel";
import AdminDocsPanel from "@/components/AdminDocsPanel";

export default function Admin() {
    const [tab, setTab] = useState("questions");
    const [stats, setStats] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [categories, setCategories] = useState({});
    const [order, setOrder] = useState([]);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [editing, setEditing] = useState(null); // question being edited
    const [adding, setAdding] = useState(null); // category being added under
    const [draft, setDraft] = useState({ text: "", category: "", order: 0 });
    const [users, setUsers] = useState([]);
    const [assessments, setAssessments] = useState([]);

    const loadAll = async () => {
        try {
            const [q, s] = await Promise.all([
                api.get("/questions"),
                api.get("/admin/stats"),
            ]);
            setQuestions(q.data.questions);
            setCategories(q.data.categories);
            setOrder(q.data.category_order);
            setStats(s.data);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const loadAudit = async () => {
        try {
            const [u, a] = await Promise.all([
                api.get("/admin/users"),
                api.get("/assessments"),
            ]);
            setUsers(u.data);
            setAssessments(a.data);
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    useEffect(() => {
        loadAll();
        loadAudit();
    }, []);

    const grouped = useMemo(() => {
        const g = {};
        order.forEach((c) => (g[c] = []));
        questions.forEach((q) => {
            (g[q.category] = g[q.category] || []).push(q);
        });
        Object.keys(g).forEach((c) =>
            g[c].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
        return g;
    }, [questions, order]);

    const startEdit = (q) => {
        setAdding(null);
        setEditing(q.id);
        setDraft({ text: q.text, category: q.category, order: q.order });
    };
    const startAdd = (cat) => {
        setEditing(null);
        setAdding(cat);
        setDraft({ text: "", category: cat, order: (grouped[cat]?.length || 0) + 1 });
    };
    const cancel = () => {
        setEditing(null);
        setAdding(null);
        setDraft({ text: "", category: "", order: 0 });
    };

    const saveDraft = async () => {
        setErr("");
        try {
            if (editing) {
                await api.put(`/questions/${editing}`, draft);
                setMsg("Question updated.");
            } else if (adding) {
                await api.post("/questions", draft);
                setMsg("Question added.");
            }
            cancel();
            await loadAll();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const deleteQuestion = async (id) => {
        if (!window.confirm("Delete this question?")) return;
        try {
            await api.delete(`/questions/${id}`);
            await loadAll();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const reseed = async () => {
        if (
            !window.confirm(
                "Replace ALL questions with the default seed set? This cannot be undone."
            )
        )
            return;
        try {
            await api.post("/questions/reseed");
            setMsg("Default questions reseeded.");
            await loadAll();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12" data-testid="admin-page">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                <div>
                    <div className="overline mb-3">Admin</div>
                    <h1 className="font-display text-4xl md:text-5xl leading-tight">
                        Risk Scan Console
                    </h1>
                </div>
                {stats && (
                    <div className="flex gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        <Stat label="Respondents" value={stats.total_respondents} />
                        <Stat label="Assessments" value={stats.total_assessments} />
                        <Stat label="Questions" value={stats.total_questions} />
                        {stats.reference_docs && (
                            <Stat
                                label="Refs loaded"
                                value={`${stats.reference_docs.smb_loaded ? "SMB" : "–"} · ${
                                    stats.reference_docs.enterprise_loaded ? "ENT" : "–"
                                }`}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className="flex gap-2 border-b border-[#2a2a2a] mb-8">
                {[
                    ["questions", "Questions"],
                    ["references", "References"],
                    ["assessments", "Assessments"],
                    ["users", "Users"],
                    ["ai-status", "AI Status"],
                    ["advisor-plans", "Advisor Plans"],
                    ["pass-claims", "Pass Claims"],
                    ["docs", "Docs"],
                ].map(([k, l]) => (
                    <button
                        key={k}
                        className={`px-4 py-3 text-xs uppercase tracking-[0.12em] ${
                            tab === k
                                ? "text-white border-b-2 border-[#00A896]"
                                : "text-[#c2c2c2] hover:text-white"
                        }`}
                        onClick={() => setTab(k)}
                        data-testid={`admin-tab-${k}`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {msg && (
                <div className="block-opportunity mb-4 text-sm" data-testid="admin-msg">
                    {msg}
                </div>
            )}
            {err && (
                <div className="block-risk mb-4 text-sm" data-testid="admin-error">
                    {err}
                </div>
            )}

            {tab === "questions" && (
                <div>
                    <div className="flex justify-end mb-6">
                        <button
                            className="btn-secondary inline-flex items-center gap-2"
                            onClick={reseed}
                            data-testid="admin-reseed-button"
                        >
                            <ArrowsClockwise size={14} weight="bold" /> Reseed defaults
                        </button>
                    </div>

                    {order.map((cat) => (
                        <section key={cat} className="mb-10">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-display text-2xl">{categories[cat]}</h2>
                                <button
                                    className="btn-ghost inline-flex items-center gap-1"
                                    onClick={() => startAdd(cat)}
                                    data-testid={`add-question-${cat}`}
                                >
                                    <Plus size={14} /> Add question
                                </button>
                            </div>
                            <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                                {(grouped[cat] || []).map((q) => (
                                    <div
                                        key={q.id}
                                        className="bg-[#0a0a0a] p-4 grid grid-cols-12 gap-3 items-start"
                                    >
                                        {editing === q.id ? (
                                            <>
                                                <textarea
                                                    rows={2}
                                                    className="input-base col-span-12 md:col-span-9"
                                                    value={draft.text}
                                                    onChange={(e) =>
                                                        setDraft({ ...draft, text: e.target.value })
                                                    }
                                                    data-testid={`edit-text-${q.id}`}
                                                />
                                                <input
                                                    type="number"
                                                    className="input-base col-span-6 md:col-span-1"
                                                    value={draft.order}
                                                    onChange={(e) =>
                                                        setDraft({
                                                            ...draft,
                                                            order: Number(e.target.value),
                                                        })
                                                    }
                                                />
                                                <div className="col-span-6 md:col-span-2 flex gap-1 justify-end">
                                                    <button className="btn-primary" onClick={saveDraft} data-testid={`save-${q.id}`}>
                                                        <FloppyDisk size={14} />
                                                    </button>
                                                    <button className="btn-secondary" onClick={cancel}>
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="col-span-12 md:col-span-1 font-mono text-xs text-[#9a9a9a]">
                                                    Q{q.order}
                                                </div>
                                                <div className="col-span-12 md:col-span-9 text-[#d4d4d4]">
                                                    {q.text}
                                                </div>
                                                <div className="col-span-12 md:col-span-2 flex gap-1 justify-end">
                                                    <button
                                                        className="btn-ghost"
                                                        onClick={() => startEdit(q)}
                                                        data-testid={`edit-${q.id}`}
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        className="btn-ghost"
                                                        onClick={() => deleteQuestion(q.id)}
                                                        data-testid={`del-${q.id}`}
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                                {adding === cat && (
                                    <div className="bg-[#0a0a0a] p-4 grid grid-cols-12 gap-3">
                                        <textarea
                                            rows={2}
                                            placeholder="New question…"
                                            className="input-base col-span-12 md:col-span-9"
                                            value={draft.text}
                                            onChange={(e) =>
                                                setDraft({ ...draft, text: e.target.value })
                                            }
                                            data-testid={`new-text-${cat}`}
                                        />
                                        <input
                                            type="number"
                                            className="input-base col-span-6 md:col-span-1"
                                            value={draft.order}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    order: Number(e.target.value),
                                                })
                                            }
                                        />
                                        <div className="col-span-6 md:col-span-2 flex gap-1 justify-end">
                                            <button
                                                className="btn-primary"
                                                onClick={saveDraft}
                                                disabled={!draft.text.trim()}
                                                data-testid={`save-new-${cat}`}
                                            >
                                                <FloppyDisk size={14} />
                                            </button>
                                            <button className="btn-secondary" onClick={cancel}>
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {tab === "references" && <ReferenceLibrary />}
            {tab === "ai-status" && <AiStatusPanel />}
            {tab === "advisor-plans" && <AdvisorPlansPanel />}
            {tab === "pass-claims" && <PassClaimsPanel />}
            {tab === "docs" && <AdminDocsPanel />}

            {tab === "assessments" && (
                <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                    {assessments.length === 0 ? (
                        <div className="bg-[#0a0a0a] p-6 text-[#c2c2c2]">
                            No assessments yet.
                        </div>
                    ) : (
                        assessments.map((a) => (
                            <a
                                key={a.id}
                                href={`/report/${a.id}`}
                                className="bg-[#0a0a0a] p-5 grid grid-cols-12 gap-3 hover:bg-[#141414]"
                                data-testid={`admin-assessment-${a.id}`}
                            >
                                <div className="col-span-12 md:col-span-5">
                                    <div className="font-display text-lg">{a.organization_name}</div>
                                    <div className="text-xs text-[#c2c2c2] mt-1">
                                        {a.user_email}
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-3 text-sm">
                                    <div className="text-[#c2c2c2]">{a.industry}</div>
                                    <div className="text-[#9a9a9a] text-xs">
                                        {a.user_count} users · {a.agreement_type}
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-2 font-mono text-2xl">
                                    {a.scores?.overall ?? 0}
                                </div>
                                <div className="col-span-12 md:col-span-2 text-xs text-[#9a9a9a] md:text-right">
                                    {new Date(a.created_at).toLocaleString()}
                                </div>
                            </a>
                        ))
                    )}
                </div>
            )}

            {tab === "users" && (
                <div className="border border-[#2a2a2a] divide-y divide-[#2a2a2a]">
                    {users.length === 0 ? (
                        <div className="bg-[#0a0a0a] p-6 text-[#c2c2c2]">No respondents yet.</div>
                    ) : (
                        users.map((u) => (
                            <div
                                key={u.id}
                                className="bg-[#0a0a0a] p-5 grid grid-cols-12 gap-3"
                                data-testid={`admin-user-${u.id}`}
                            >
                                <div className="col-span-12 md:col-span-4 font-display text-lg">
                                    {u.name}
                                </div>
                                <div className="col-span-12 md:col-span-4 text-[#d4d4d4]">
                                    {u.email}
                                </div>
                                <div className="col-span-12 md:col-span-2 text-[#c2c2c2]">
                                    {u.organization || "—"}
                                </div>
                                <div className="col-span-12 md:col-span-2 text-xs text-[#9a9a9a] md:text-right">
                                    {new Date(u.created_at).toLocaleString()}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-[#0a0a0a] p-4 min-w-[120px]">
            <div className="text-[12px] uppercase tracking-widest text-[#9a9a9a]">{label}</div>
            <div className="font-mono text-3xl mt-1">{value}</div>
        </div>
    );
}
